import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import {
  IGetLifeChoiceParams,
  IGetLifeChoicesParams,
  IGetCareerParams
} from "types/api/lifeChoices";
import { IBlocksResponse } from "types/block";
import { ICareer, IResponse } from "types/lifeChoice";
import axiosInstance from "utils/axios";
import { decrypt } from "utils/crypto";

export const lifeChoiseBlocksApi = createApi({
  reducerPath: "lifeChoiseBlocksApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${process.env.REACT_APP_SERVER_URL}`,
    prepareHeaders: (headers) => {
      const tenant = window.location.host.split('.')[0];
      headers.set("Authorization", "Bearer " + (localStorage.getItem("token") || ""));
      headers.set('x-tenant', tenant);

      return headers;
    },
  }),
  endpoints: (builder) => ({
    getLifeChoices: builder.query<{ data: IBlocksResponse, hasMore?: boolean }, IGetLifeChoicesParams>({
      query: ({ pageSize, page, searchTerm }) => {
        if (!page) {
          page = 1;
        }
        let search = "";
        if (page) {
          search = `${search}page=${page}`;
        }
        if (searchTerm) {
          search = `&searchTerm=${searchTerm}`;
        }
        if (pageSize) {
          search = `${search}&pageSize=${pageSize}`;
        }
        return `/major/life_choices?${search}`;
      }
    }),
    getLifeChoice: builder.query<IResponse, IGetLifeChoiceParams>({
      query: ({ id }) => `/major/life_choices/${id}`,
    }),
    getCareer: builder.query<ICareer, IGetCareerParams>({
      query: ({ id }) => `/career/${id}`,
    }),
  }),
});

export const { useGetLifeChoiceQuery, useGetLifeChoicesQuery, useGetCareerQuery } =
  lifeChoiseBlocksApi;

export const useLifeChoices = ({ page, pageSize, searchTerm }: IGetLifeChoicesParams) => {
  const { data, error, isLoading } = useGetLifeChoicesQuery({ page, pageSize, searchTerm });
  const [lifeChoices, setLifeChoices] = useState<IBlocksResponse | null>(null);

  useEffect(() => {
    if (data?.data) {
      setLifeChoices(decrypt(data.data));
    }
  }, [data]);
  return { data: lifeChoices, error, isLoading, hasMore: data?.hasMore };
};

export const useLifeChoice = ({ id }: IGetLifeChoiceParams) => {
  const { data, error, isLoading } = useGetLifeChoiceQuery({ id });
  const [lifeChoice, setLifeChoice] = useState<IResponse | null>(null);

  useEffect(() => {
    if (data?.data) {
      setLifeChoice(decrypt(data?.data));
    }
  }, [data]);
  return { data: lifeChoice, error, isLoading };
};


export const useSearchMajor = ({ searchTerm }: { searchTerm: string }) => {
  const [data, setData] = useState({
    data: [],
    error: null,
    isLoading: true,
  });

  useEffect(() => {
    if (searchTerm)
      (async () => {
        try {
          const response = await axiosInstance.get('/major/life_choices', {
            params: {
              searchTerm
            }
          }).then((res) => res.data);
          setData({ data: decrypt(response.data), error: null, isLoading: false });
        } catch (err: any) {
          setData({ data: [], error: err.message, isLoading: false });
        }
      })();
  }, [searchTerm]);

  return { ...data };
}

const getSuggestions = async () => {
  try {
    const response = await axiosInstance.get('/major/suggestions').then((res) => res.data);
    return response.data;
  } catch (err: any) {
    return [];
  }
};


export function useSuggestions() {
  const { data, error, isLoading, refetch } = useQuery({
    queryKey: ["suggestions"],
    queryFn: getSuggestions,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
  const [suggestions, setSuggestions] = useState<any[] | null>(null);

  useEffect(() => {
    if (data) {
      setSuggestions(decrypt(data));
    }
  }, [data]);

  return {
    data: suggestions,
    error,
    isLoading,
    refetch,
  }
}